import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";

/**
 * ===========================================================================
 *  SUIVI DES RESPONSABLES COMMANDE (15/10/2026)
 * ===========================================================================
 *
 *  Depuis que Bourgogne a deux responsables, chaque commande est donnee a
 *  l'un d'eux (voir functions/src/services/repartition.service.ts). Ce
 *  tableau dit, par personne et sur la periode : combien de commandes,
 *  combien terminees, en combien de temps, combien prises en charge en
 *  retard ou reprises par l'autre, et ce qu'elles ont coute en
 *  remboursements et en ventes perdues.
 *
 *  Seules les commandes ATTRIBUEES comptent : avant le 15/10/2026, aucune
 *  commande n'avait de responsable. Le serveur garde le calcul 10 minutes.
 * ===========================================================================
 */

type Jours = 1 | 7 | 30;
type Suivi = Awaited<ReturnType<typeof api.orders.suiviResponsables>>;

const PERIODES: Array<{ jours: Jours; libelle: string }> = [
  { jours: 1, libelle: "Aujourd'hui" },
  { jours: 7, libelle: "7 jours" },
  { jours: 30, libelle: "30 jours" },
];

const dh = (centimes: number) =>
  `${(centimes / 100).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} DH`;

const teinte = (ok: boolean, moyen: boolean) =>
  ok ? "text-emerald-700" : moyen ? "text-amber-700" : "text-red-700";

export default function PerformanceResponsables({
  storeFilter,
  nomDuMagasin,
}: {
  storeFilter: string;
  nomDuMagasin: (storeId: string | null) => string;
}) {
  const [jours, setJours] = useState<Jours>(1);
  const [donnees, setDonnees] = useState<Suivi | null>(null);
  const [chargement, setChargement] = useState(false);

  useEffect(() => setDonnees(null), [jours, storeFilter]);

  useEffect(() => {
    let annule = false;
    const charger = () => {
      setChargement(true);
      api.orders
        .suiviResponsables(jours, storeFilter)
        .then((r) => !annule && setDonnees(r))
        .catch(() => {
          /* un confort : son absence ne casse pas le tableau de bord */
        })
        .finally(() => !annule && setChargement(false));
    };
    charger();
    const arret = intervalleVisible(charger, 10 * 60_000);
    return () => {
      annule = true;
      arret();
    };
  }, [jours, storeFilter]);

  const lignes = donnees?.lignes || [];
  const delai = donnees?.delaiMinutes || 10;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Responsables commande
          </p>
          {chargement && <span className="text-[11px] text-gray-400">mise à jour…</span>}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {PERIODES.map((p) => (
            <button
              key={p.jours}
              type="button"
              onClick={() => setJours(p.jours)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                jours === p.jours ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {p.libelle}
            </button>
          ))}
        </div>
      </div>

      {donnees && donnees.nonAttribuees > 0 && (
        <p className="mx-4 md:mx-5 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          ⚠️ {donnees.nonAttribuees} commande{donnees.nonAttribuees > 1 ? "s" : ""} en cours sans
          responsable : personne n'était en service quand elle{donnees.nonAttribuees > 1 ? "s sont" : " est"} arrivée
          {donnees.nonAttribuees > 1 ? "s" : ""}.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">Responsable</th>
              <th className="px-3 py-2.5 text-left font-semibold" title="Commandes données à ce responsable sur la période">
                Commandes
              </th>
              <th
                className="px-3 py-2.5 text-left font-semibold"
                title={`Commande donnée → « Je m'en occupe ». Rouge au-delà de ${delai} min.`}
              >
                Prise en charge
              </th>
              <th className="px-3 py-2.5 text-left font-semibold" title="Commande donnée → commande prête, moyenne">
                Préparation
              </th>
              <th
                className="px-3 py-2.5 text-left font-semibold"
                title={`Prises en charge après ${delai} min, et commandes que l'autre a dû reprendre`}
              >
                Retards
              </th>
              <th className="px-3 py-2.5 text-left font-semibold" title="Commandes de ce responsable remboursées au client">
                Remboursements
              </th>
              <th className="px-3 py-2.5 text-left font-semibold" title="Articles retirés des commandes de ce responsable">
                Ventes perdues
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lignes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  {donnees
                    ? "Aucune commande attribuée sur cette période (la répartition date du 15/10/2026)."
                    : "Chargement…"}
                </td>
              </tr>
            )}
            {lignes.map((l) => (
              <tr key={l.uid} className="hover:bg-gray-50/70">
                <td className="px-3 py-2.5">
                  <p className="font-semibold text-gray-900">{l.nom}</p>
                  {storeFilter === "ALL" && <p className="text-[11px] text-gray-400">{nomDuMagasin(l.storeId)}</p>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className="font-bold text-gray-900">{l.commandes}</span>
                  <span className="text-[11px] text-gray-400">
                    {" "}
                    · {l.terminees} terminée{l.terminees > 1 ? "s" : ""}
                    {l.enCours ? ` · ${l.enCours} en cours` : ""}
                    {l.annulees ? ` · ${l.annulees} annulée${l.annulees > 1 ? "s" : ""}` : ""}
                  </span>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {l.priseEnChargeMoyMin === null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span
                      className={`font-bold ${teinte(l.priseEnChargeMoyMin <= delai / 2, l.priseEnChargeMoyMin <= delai)}`}
                    >
                      {l.priseEnChargeMoyMin} min
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {l.preparationMoyMin === null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span className={`font-bold ${teinte(l.preparationMoyMin <= 20, l.preparationMoyMin <= 30)}`}>
                      {l.preparationMoyMin} min
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {l.retards || l.reprisesSubies ? (
                    <span className="font-bold text-red-700">
                      {l.retards} en retard
                      {l.reprisesSubies ? ` · ${l.reprisesSubies} reprise${l.reprisesSubies > 1 ? "s" : ""} par l'autre` : ""}
                    </span>
                  ) : (
                    <span className="text-gray-300">0</span>
                  )}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {l.remboursees ? (
                    <>
                      <span className="font-bold text-red-700">{dh(l.remboursementCentimes)}</span>
                      <span className="text-[11px] text-gray-400"> · {l.remboursees} cmd</span>
                    </>
                  ) : (
                    <span className="text-gray-300">0</span>
                  )}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {l.ventePerdueCentimes ? (
                    <span className="font-bold text-red-700">{dh(l.ventePerdueCentimes)}</span>
                  ) : (
                    <span className="text-gray-300">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
